import { useCallback, useEffect, useState } from "react";
import { CalendarClock, TriangleAlert } from "lucide-react";

import { api, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useToast } from "../context/ToastContext.js";
import { CardDescription, CardTitle } from "./ui/card.js";
import { GlowCard } from "./ui/glow-card.js";
import ShiftNotSetBanner from "./ShiftNotSetBanner.js";
import { Textarea } from "./ui/textarea.js";
import { Badge } from "./ui/badge.js";
import { AsyncButton } from "./ui/async-button.js";
import { DateInput } from "./ui/date-input.js";
import { TimeInput } from "./ui/time-input.js";

export interface ShiftDayRequest {
  id: string;
  startDate: string;
  endDate: string;
  requestedStartHHMM: string;
  requestedEndHHMM: string;
  remark: string;
  status: "PENDING" | "APPROVED" | "REJECTED";
  approverRemark: string | null;
}

const STATUS_TONE: Record<ShiftDayRequest["status"], "warning" | "success" | "error"> = {
  PENDING: "warning", APPROVED: "success", REJECTED: "error",
};

/** A working day. A request may run longer, never shorter. The server enforces it too. */
const SHIFT_HOURS = 9;
const DAY_END = "23:59";

const pad2 = (n: number) => String(n).padStart(2, "0");
const minutesOf = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3));
const hhmmOf = (mins: number) => `${pad2(Math.floor(mins / 60))}:${pad2(mins % 60)}`;

/**
 * The earliest end a nine-hour day starting at `start` can have -- the first time
 * the To field offers, and the default. Anything later is overtime and allowed;
 * anything earlier is a short day by the back door, so it is not offered at all.
 */
const earliestEnd = (start: string) => hhmmOf(minutesOf(start) + SHIFT_HOURS * 60);

/** The last start from which a full day still fits before midnight. */
const LATEST_START = hhmmOf(minutesOf(DAY_END) - SHIFT_HOURS * 60);

/** YYYY-MM-DD for today, so the date field cannot be set to a day already gone. */
function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * The employee's own view of their shift: a prompt when nobody has set one, and the
 * form for asking to work different hours on a single day.
 *
 * Both live here rather than in the attendance page's stats bar because that bar is
 * a row of read-only figures, and because these are the two things an employee can
 * actually *do* about their shift.
 */
export function MyShiftRequestsCard({ employeeId }: { employeeId: string }) {
  const { show } = useToast();
  const [requests, setRequests] = useState<ShiftDayRequest[]>([]);
  const [startDate, setStartDate] = useState(todayKey());
  // Defaults to the same day, so a one-day ask needs no second date picked.
  const [endDate, setEndDate] = useState(todayKey());
  const [from, setFrom] = useState("09:00");
  const [to, setTo] = useState(earliestEnd("09:00"));
  const [remark, setRemark] = useState("");
  // Set when they try to send without a reason, cleared as soon as they type one.
  // Only after an attempt: a field that is red before anyone has touched it reads
  // as a mistake already made.
  const [remarkMissing, setRemarkMissing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get<ShiftDayRequest[]>(`/shift-day-requests?employeeId=${employeeId}`);
      setRequests(data);
    } catch (e) {
      logError("MyShiftRequestsCard.load", e);
    }
  }, [employeeId]);

  useEffect(() => { void load(); }, [load]);

  async function submit() {
    if (!remark.trim()) {
      setRemarkMissing(true);
      show("Say why you need these hours", "error");
      throw new Error("validation");
    }
    if (endDate < startDate) { show("The last day cannot be before the first", "error"); throw new Error("validation"); }
    try {
      await api.post("/me/shift-day-requests", {
        startDate, endDate, requestedStartHHMM: from, requestedEndHHMM: to, remark: remark.trim(),
      });
      show("Sent for approval", "success");
      setRemark("");
      void load();
    } catch (e) {
      logError("MyShiftRequestsCard.submit", e);
      show(errMsg(e) ?? "Could not send that", "error");
      throw e;
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <ShiftNotSetBanner />

      {/* The one thing on this tab a person acts on, so it gets the edge glow --
          lightest step, with the interior hover kept low and no shadow. */}
      <GlowCard glow="ultra" spotlight="low" size="lg" className="shadow-none">
        <div className="mb-4 flex flex-col gap-1">
          <CardTitle className="flex items-center gap-2 text-[15px]">
            <CalendarClock size={16} />
            Need different hours for a day or a period?
          </CardTitle>
          <CardDescription>
            Ask your manager, senior manager or HR to agree different hours. Once approved they
            are your hours for that period; your usual shift returns afterwards.
          </CardDescription>
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-surface-500">First day</span>
              <DateInput
                value={startDate}
                min={todayKey()}
                onChange={(v) => {
                  setStartDate(v);
                  // Keeps a one-day ask correct without the employee touching the
                  // second field, and stops the range ever being backwards.
                  if (endDate < v) setEndDate(v);
                }}
                className="w-44"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-surface-500">Last day</span>
              <DateInput value={endDate} min={startDate} onChange={(v) => setEndDate(v)} className="w-44" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-surface-500">From</span>
              <TimeInput
                value={from}
                max={LATEST_START}
                aria-label="Start time"
                onChange={(v) => {
                  setFrom(v);
                  // Moving the start forward drags the end with it, so the window
                  // can never silently become shorter than a working day.
                  if (minutesOf(to) < minutesOf(earliestEnd(v))) setTo(earliestEnd(v));
                }}
                className="w-32"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-surface-500">To</span>
              {/* Nothing before the nine-hour mark is selectable; later is overtime. */}
              <TimeInput
                value={to}
                min={earliestEnd(from)}
                max={DAY_END}
                aria-label="End time"
                onChange={setTo}
                className="w-32"
              />
            </label>
            <span className="pb-2 text-[12px] font-medium text-surface-500">
              {((minutesOf(to) - minutesOf(from)) / 60).toFixed(2).replace(/\.?0+$/, "")} hrs
              <span className="ml-1 font-normal text-surface-400">&middot; {SHIFT_HOURS} hrs minimum</span>
            </span>
          </div>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-surface-500">
              Why <span className="text-[var(--color-error)]" aria-hidden="true">*</span>
            </span>
            <Textarea
              value={remark}
              required
              aria-required="true"
              aria-invalid={remarkMissing || undefined}
              tone={remarkMissing ? "error" : "default"}
              onChange={(e) => { setRemark(e.target.value); if (remarkMissing) setRemarkMissing(false); }}
              placeholder="Train gets in at 10:30, I would rather work the full day than take leave."
              rows={2}
            />
            {remarkMissing && (
              // The toast is gone in seconds; the approver's question is not.
              <span className="text-[12px] font-medium text-[var(--color-error-fg)]">
                Say why you need these hours &mdash; the approver decides on this alone.
              </span>
            )}
          </label>
          <div className="flex justify-end">
            <AsyncButton size="sm" onClick={() => submit()}>Send for approval</AsyncButton>
          </div>

          {requests.length > 0 && (
            <div className="flex flex-col gap-2 border-t border-surface-200 pt-3">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-surface-500">Your requests</span>
              {requests.slice(0, 5).map((r) => (
                <div key={r.id} className="flex flex-wrap items-center gap-2 text-[13px]">
                  <Badge tone={STATUS_TONE[r.status]}>{r.status.toLowerCase()}</Badge>
                  <span className="font-semibold tabular-nums">
                    {r.startDate === r.endDate ? r.startDate : `${r.startDate} → ${r.endDate}`}
                  </span>
                  <span className="tabular-nums">{r.requestedStartHHMM}–{r.requestedEndHHMM}</span>
                  <span className="text-surface-500">{r.remark}</span>
                  {r.approverRemark && <span className="text-surface-500">&mdash; {r.approverRemark}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </GlowCard>
    </div>
  );
}

export default MyShiftRequestsCard;
